import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, Outlet } from 'react-router-dom';

import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

// src
import SettingsFilter from './SettingsFilter';
import Loader from 'components/common/Loader';
import DeclinedTransctionsBanner from 'components/common/DeclinedTransactionsBanner';

// contexts
import CurrentUserContext from 'contexts/CurrentUserContext';
import PretaxContext from 'contexts/PretaxContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';

// hooks
import useQueryProfile from 'hooks/useQueryProfile';
import useQueryPretaxAccounts from 'hooks/useQueryPretaxAccounts';

import { FeatureFlag } from 'constants/featureFlag';
import { PretaxAccountV2Type } from 'constants/accounts';
import { Employee, Company, EmployeeWallet } from './types';
import { getShowDependentForV1, getShowDependentForV2 } from 'utils/dependent';
import { hasAlegeusHsaAccount } from 'utils/pretaxAccount';
import { PretaxAccount, PretaxWallet } from 'contexts/PretaxContext/types';

const Container = styled.div`
  display: flex;
  max-width: 880px;
  margin: 0 auto;
  padding: 22px 16px 0 16px;
  flex-direction: column;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    flex-direction: row;
    gap: 99px;
    padding: 80px 0 0;
  }
`;

const Main = styled.div`
  flex: 1;
`;

const SettingsLayout = ({ children }: { children?: React.ReactNode }) => {
  const location = useLocation();
  const active_path = location.pathname;

  const feature_flag = useContext(FeatureFlagContext);
  const is_unit_forma_card_enabled = feature_flag?.flag(
    FeatureFlag.UNIT_FORMA_CARD
  );
  const is_pretax_v2 = feature_flag?.flag(
    FeatureFlag.ALEGEUS_MIGRATED_CUSTOMER,
    false
  );
  const is_stripe_card_disabled = feature_flag?.flag(
    FeatureFlag.STRIPE_CARD_DISABLED
  );

  const { company, employee }: { employee: Employee; company: Company } =
    useContext(CurrentUserContext);

  const {
    alegeus_pretax_accounts,
    pretax_accounts,
    updateWallets,
    isLoadingPretax,
    isAlegeusHSA,
  }: {
    alegeus_pretax_accounts: Array<PretaxAccount>;
    pretax_accounts: Employee['employee_wallets'];
    updateWallets: (employee_wallets: Array<EmployeeWallet>) => void;
    isLoadingPretax: boolean;
    isAlegeusHSA: boolean;
  } = useContext(PretaxContext);

  const [employee_wallets_v3, setEmployeeWalletsV3] = useState<
    Array<PretaxWallet>
  >([]);

  const { data: pretax_accounts_data, isLoading: is_pretax_accounts_loading } =
    useQueryPretaxAccounts({
      is_cdh_enabled: isAlegeusHSA,
    });

  const { data: profile_data, isLoading: is_profile_v3_loading } =
    useQueryProfile({
      isEnabled: Boolean(employee.id && is_pretax_v2),
      use_v3_profile: is_pretax_v2,
    });

  useEffect(() => {
    if (is_pretax_v2 && !is_profile_v3_loading && profile_data) {
      const {
        employee: { employee_wallets: employee_wallets_v3 },
      } = profile_data;
      setEmployeeWalletsV3(employee_wallets_v3);
      updateWallets(employee_wallets_v3);
    }
  }, [profile_data, is_pretax_v2, is_profile_v3_loading, updateWallets]);

  const show_dependent = is_pretax_v2
    ? getShowDependentForV2(employee_wallets_v3)
    : getShowDependentForV1(alegeus_pretax_accounts);

  if (isLoadingPretax || is_pretax_accounts_loading) {
    return <Loader active />;
  }

  const hasNonHSAPretax = pretax_accounts.some(
    (acct) =>
      acct.wallet_type_id === PretaxAccountV2Type.PARKING ||
      acct.wallet_type_id === PretaxAccountV2Type.TRANSIT ||
      acct.wallet_type_id === PretaxAccountV2Type.FSA ||
      acct.wallet_type_id === PretaxAccountV2Type.LPFSA ||
      acct.wallet_type_id === PretaxAccountV2Type.DCFSA
  );

  const has_alegeus_hsa_account = hasAlegeusHsaAccount(
    pretax_accounts_data?.pretax_accounts
  );

  return (
    <>
      {hasNonHSAPretax && <DeclinedTransctionsBanner />}
      <Container>
        <SettingsFilter
          active_path={active_path}
          company={company}
          employee={employee}
          has_pretax_v2_accounts={pretax_accounts.length > 0}
          hasAlegeus={alegeus_pretax_accounts.length > 0}
          hasAlegeusHsaAccount={has_alegeus_hsa_account}
          is_stripe_card_disabled={is_stripe_card_disabled}
          is_unit_forma_card_enabled={is_unit_forma_card_enabled}
          show_dependent={show_dependent}
        />
        <Main>{children ?? <Outlet />}</Main>
      </Container>
    </>
  );
};

export default SettingsLayout;
